
import React from "react";
import * as Hooks from "../../../../../hooks";
import { i18nManager } from "../../../../../i18n";
import { Schemes, Utils } from "../../../../../utils";
import { Elements } from "../../../../shared/Elements";
import * as ReactRouterDom from "react-router-dom";
import { ConfirmDialog } from "../../../../shared/ConfirmDialog";
import LoadingGif from "../../../assets/images/loading_circle.gif";
import { Button, ButtonManageRef, Checkbox, Classname, DateInput, Dropdown, Form, FormControl, NumberInput, Scheme, Subscriber, TextInput, useSubscription } from "@rivtn/noseur";
import { ApiResponseData, BaseService, CountryService, ICountry, IMember, IHost, SanitizedResponse, HostRole, HostService } from "../../../../../services";
import { CreateTournamentSectionProps, CreateTournamentSection } from "../CreateTournament";

function Registration(props: CreateTournamentSectionProps) {

    const { active, tournamentEditionState } = props;
    const [themeMode, _] = Hooks.useThemeMode();
    const countryService = CountryService.getInstance();
    const [countries, setCountries] = React.useState<ICountry[]>();
    const labels = i18nManager.Labels.host.create_tournament.registration;
    const [tournamentEdition, setTournamentEdition] = tournamentEditionState;
    const [registrationCountries, setRegistrationCountries] = React.useState<string[]>([]);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);

    React.useEffect(() => {
        countryService.queryCountries({ name: "" }).then(({ content }: SanitizedResponse<ICountry>) => {
            setCountries(content);
            setRegistrationCountries((Object.keys(tournamentEdition.registration ?? {}) ?? [(tournamentEdition.country_code ?? "NGA")]).map((c) => i18nManager.getCurrencyCountryCode(c)));
        }).catch(BaseService.reportError);
    }, []);

    React.useEffect(() => {
        if (registrationCountries.includes(tournamentEdition.country_code!)) return;
        setRegistrationCountries([(tournamentEdition.country_code!)]);
    }, [tournamentEdition.country_code]);

    React.useEffect(() => {
        if (!registrationCountries.length) return;
        setCountries([...(countries ?? [])].map((country) => {
            (country as any).not_selectable = registrationCountries.includes(country.code);
            return country;
        }));
    }, [registrationCountries]);

    return (<div className="create-tournament-f flow details" ref={props.ref}>
        {!active && (<div className="overlay" onClick={() => props.setActiveSection(CreateTournamentSection.REGISTRATION, undefined, true)} />)}
        <span className="title">{labels.title}</span>
        <Form className="content" scheme={Schemes.OMNEHOST}>
            <Checkbox label={labels.bear_reg_cost} defaultChecked={tournamentEdition.registration_settings?.bear_platform_fee_cost} onChecked={(v) => {
                setTournamentEdition({ ...tournamentEdition, registration_settings: { ...tournamentEdition.registration_settings as any, bear_platform_fee_cost: v } });
            }} />
            <Checkbox label={labels.show_reg_count} defaultChecked={tournamentEdition.registration_settings?.show_participants_count_on_listing} onChecked={(v) => {
                setTournamentEdition({ ...tournamentEdition, registration_settings: { ...tournamentEdition.registration_settings as any, show_participants_count_on_listing: v } });
            }} />
            <Checkbox label={labels.prevent_already_engaged_participants} defaultChecked={tournamentEdition.registration_settings?.prevent_participants_engaged_in_other_tournaments} onChecked={(v) => {
                setTournamentEdition({ ...tournamentEdition, registration_settings: { ...tournamentEdition.registration_settings as any, prevent_participants_engaged_in_other_tournaments: v } });
            }} />
            <p style={{ fontWeight: "bold", marginTop: 20, }}>{labels.registration_fees}</p>
            <Dropdown fill options={countries} optionMap={{ label: "name", value: "code" }}
                formControlProps={{ isFieldset: true, label: labels.add_another_country }}
                scheme={Schemes.OMNEHOST} onSelectOption={(country: ICountry) => {
                    setRegistrationCountries([...registrationCountries, country.code]);
                    return true;
                }} />
            <hr style={{ marginTop: 30, marginBottom: 20, width: "calc(100% - 2px)" }} />
            <div style={{ flex: 1 }}>
                {registrationCountries.map((countryCode) => {
                    const countryName = i18nManager.getCountryName(countryCode);
                    const currencySign = i18nManager.getCountryCurrencySign(countryCode);
                    const countryCurrencyCode = i18nManager.getCountryCurrency(countryCode);

                    return (<div key={countryCode} style={{ display: "flex", justifyContent: "center", marginBottom: 15 }}>
                        <FormControl isFieldset fill label={(<label>{countryName} ({countryCurrencyCode}) <span style={{ color: "#FF7171" }}>*</span></label>)} style={{ flex: 1 }}
                            leftContent={<span>{currencySign}</span>}>
                            <NumberInput fill defaultValue={(tournamentEdition.registration?.[countryCurrencyCode]?.fee_amount ?? 0) / 1000000} onInputComplete={(value: string) => {
                                updateTournamentEditionRegistration(countryCode, parseInt(value) * 1000000);
                            }} />
                        </FormControl>
                        <FormControl isFieldset fill label={labels.max_participants} style={{ flex: 0.7, marginLeft: 20 }}>
                            <NumberInput fill defaultValue={tournamentEdition.registration?.[countryCurrencyCode]?.max_participants} onInputComplete={(value: string) => {
                                updateTournamentEditionRegistration(countryCode, undefined, parseInt(value));
                            }} />
                        </FormControl>
                        <i className={Classname.build("fa fa-times link danger", { "noseur-disabled": countryCode === tournamentEdition.country_code })} style={{ fontSize: 20, margin: 15 }} onClick={() => {
                            registrationCountries.splice(registrationCountries.indexOf(countryCode, 1));
                            setRegistrationCountries([...registrationCountries]);
                        }} />
                    </div>);
                })}
            </div>
            <p>{labels.note_max_participants_optional}</p>
            <p style={{ color: "#FF7171", marginTop: -10 }}>{labels.note_tour_available_in_listed_countries}</p>
        </Form>
        <div className="controls">
            <Button textOnly scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.prev} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
                e.manageRef?.setLoadingState(true);
                props.setActiveSection(CreateTournamentSection.DATES, () => {
                    e.manageRef?.setLoadingState(false);
                });
            }} />
            <Button scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.next} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
                e.manageRef?.setLoadingState(true);
                props.setActiveSection(CreateTournamentSection.CUSTOM_FIELDS, () => {
                    e.manageRef?.setLoadingState(false);
                });
            }} />
        </div>
    </div>);

    function updateTournamentEditionRegistration(countryCode: string, feeAmount?: number, maxParticipants?: number) {
        if (!tournamentEdition.registration) tournamentEdition.registration = {};
        const countryCurrency = i18nManager.getCountryCurrency(countryCode);
        tournamentEdition.registration[countryCurrency] = {
            fee_amount: feeAmount ?? tournamentEdition.registration[countryCurrency]?.fee_amount,
            max_participants: maxParticipants ?? tournamentEdition.registration[countryCurrency]?.max_participants,
        };
        setTournamentEdition({ ...tournamentEdition, registration: { ...tournamentEdition.registration } });
    }

}

export default Registration;
